import { AppShell, Card, Flex, SimpleGrid, Text, Title } from "@mantine/core";
import { useInterval } from "@mantine/hooks";
import * as React from "react";
import { PlaybackControls } from "@/components/carousel/components/playback-controls";
import { HUD } from "@/components/game-player/components/hud";
import { Instruction } from "@/components/game-player/components/instruction";
import { Interaction } from "@/components/game-player/components/interaction";
import { PauseButton } from "@/components/game-player/components/pause-button";
import { Scroller } from "@/components/game-player/components/scroller";
import { HotkeyConfigButton } from "@/components/hotkey-config-modal";
import type { Log } from "@/game/events/log";

import { createFileRoute } from "@tanstack/react-router";
import { Page } from "@/components/page";
import { ExitButton } from "@/components/exit-button";
import { FullScreenButton } from "@/components/full-screen-button";
import { MetronomeBarCounter } from "@/components/game-player/components/metronome-bar-counter";
import { GameObjectManager } from "@/game2/game-object-manager";
import { BeatEmitter } from "@/game2/game-objects/beat-emitter";

export const Route = createFileRoute("/_authenticated/_subscribed/hud")({
  component: HUDTestingRouteComponent,
});

function HUDTestingRouteComponent() {
  const [logs, setLogs] = React.useState<Log[]>([]);
  const [paused, setPaused] = React.useState(false);

  const gameObjectManagerRef = React.useRef(new GameObjectManager());
  const beatEmitterRef = React.useRef(new BeatEmitter());

  React.useEffect(() => {
    const controller = new AbortController();
    const gameObjectManager = gameObjectManagerRef.current;
    const beatEmitter = beatEmitterRef.current;

    beatEmitter.onBeatQueued((beat) => {
      gameObjectManager.add(beat);
    }, controller.signal);

    return () => {
      controller.abort();
    };
  }, []);

  const logGeneratorInterval = useInterval(() => {
    setLogs((logs) => [
      ...logs,
      {
        timestamp: new Date().toISOString(),
        message: `this is message **${logs.length + 1} test** and **${
          logs.length + 1
        }**`,
      },
    ]);
  }, 1000);

  React.useEffect(() => {
    logGeneratorInterval.start();
    return logGeneratorInterval.stop;
  }, [logGeneratorInterval]);

  return (
    <Page title="HUD">
      <AppShell
        h="100%"
        styles={{
          main: {
            height: "100%",
          },
        }}
        header={{
          height: 50,
        }}
      >
        <AppShell.Header>
          <Flex justify="space-between" p="xs">
            <Flex h="100%" justify="flex-start" align="center" gap="md">
              <ExitButton />
              <Title size="h3">HUD</Title>
            </Flex>
            <Flex gap="sm" align="center">
              <HotkeyConfigButton />
              <PlaybackControls />
              <FullScreenButton />
            </Flex>
          </Flex>
        </AppShell.Header>
        <AppShell.Main>
          <HUD
            prompt={{ message: "Test Prompt" }}
            information={
              <Card>
                <SimpleGrid cols={2} spacing="xs">
                  <Text>Grip:</Text>
                  <Text>normal</Text>
                  <Text>Style:</Text>
                  <Text>full</Text>
                </SimpleGrid>
              </Card>
            }
            persistantActions={
              <PauseButton
                disabled={false}
                paused={paused}
                onClick={() => setPaused(!paused)}
              />
            }
            logs={logs}
            instruction={
              <Instruction
                paused={paused}
                duration={2}
                label="This is a label"
                attributes={[
                  { label: "hello world", value: "60" },
                  { label: "thing2", value: "normal" },
                  { label: "thing3", value: "wow" },
                  { label: "thing4", value: "full" },
                ]}
              />
            }
            interaction={
              <Interaction
                paused={paused}
                description="This is a description of what to do."
                autoselect={{
                  time: 5,
                  index: 0,
                }}
                options={[
                  { label: "Option 1", onClick() {} },
                  { label: "Option 2", onClick() {} },
                ]}
              />
            }
            metronome={
              <MetronomeBarCounter beatEmitter={beatEmitterRef.current} />
            }
            chat={<div>Chat Placeholder</div>}
          />
          <Scroller
            tags={["puppies"]}
            mediaTypes={["image"]}
            paused={paused}
            blur={false}
          />
        </AppShell.Main>
      </AppShell>
    </Page>
  );
}
